import { Size } from '@ps/web-core-store';
import type { Crm } from '@ps/web-martech-cms-types';
import { Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import CrmZone from '@swf/components/CrmZone/CrmZone';
import type { ModalContextValue } from '@swf/components/StarsModal/Context';
import { ModalContext } from '@swf/components/StarsModal/Context';
import type { Variant } from '@swf/components/StarsModal/components/CloseButton';
import CloseButton from '@swf/components/StarsModal/components/CloseButton';
import Header from '@swf/components/StarsModal/components/Header/Header';
import { CRMZone } from '@swf/core/enums';
import useSearchParams from '@swf/core/hooks/useSearchParams';
import { select } from '@swf/core/store';

import { TXTCLI_My_News } from './StarsCrm.messages.json';
import styles from './StarsCrm.scss';
import { zoneStyles } from './constants';

export interface SearchParams {
  className?: string;
  zoneId?: keyof typeof zoneStyles;
  componentType: Crm['component_type'];
  icon?: Variant;
  scrolling?: string;
  size?: Size;
  headerVariant?: string;
}

const getSearchParamsClassName = (searchParams: SearchParams | undefined) =>
  classNames(searchParams?.zoneId && zoneStyles[searchParams.zoneId], searchParams?.className);

const StarsCrmWrapper: React.FC<{ className?: string; children: React.ReactElement }> = ({
  className,
  children
}) => {
  const embeddedModal = useSelector(select.location.isEmbeddedModal);

  return embeddedModal ? (
    <div className={classNames(styles.wrapper, className)}>{children}</div>
  ) : (
    children
  );
};

const StarsCrm: React.FC = () => {
  const { setStyle } = useContext<ModalContextValue>(ModalContext);
  const { zoneID } = useParams<{ zoneID: string }>();
  const searchParams = useSearchParams<SearchParams | undefined>();

  const searchParamsClassName = getSearchParamsClassName(searchParams);

  const {
    headerVariant,
    icon,
    componentType,
    scrolling,
    zoneId: searchParamsZoneId
  } = searchParams || {};
  const displayMyNewsHeader = searchParamsZoneId === CRMZone.MY_NEWS;

  useEffect(() => {
    setStyle({
      size: searchParams?.size || Size.MEDIUM,
      className: classNames(styles.modal, searchParamsClassName)
    });
  }, [searchParams, searchParamsClassName, setStyle]);

  return (
    <StarsCrmWrapper className={searchParamsClassName}>
      <>
        {displayMyNewsHeader && (
          <Header
            className={classNames(
              styles.header,
              headerVariant && styles[headerVariant as keyof typeof styles]
            )}
          >
            <Translation {...TXTCLI_My_News} />
          </Header>
        )}
        {zoneID !== CRMZone.STARS_REWARDS_OPT_IN && (
          <CloseButton
            variant={icon}
            className={displayMyNewsHeader ? styles.withHeader : styles.withoutHeader}
          />
        )}
        <CrmZone
          zoneId={zoneID}
          componentType={componentType || 'Single'}
          scrolling={scrolling}
          dynamicHeight
        />
      </>
    </StarsCrmWrapper>
  );
};

export default StarsCrm;
